/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { Dispatch, PropsWithChildren, SetStateAction, createContext, useState, useEffect, useCallback, useMemo } from 'react'

import { Nullable } from 'src/types/main'

import {
  // DonorIdentity,
  getLocalStorageDonorIdentity,
  DonorIdentity,
  setLocalStorageDonorIdentity
} from './donation-identity-collector.services'

type DonationIdentityCollectorContextType = {
  donorIdentity: Nullable<Partial<DonorIdentity>>
  donorIdentityId: Nullable<string>
  identityIsValid: boolean
  setDonorIdentity: (donorIdentity: Partial<DonorIdentity>) => void
  setDonorIdentityId: Dispatch<SetStateAction<Nullable<string>>>
  storePersistentDonorIdentityData: (donorIdentityId: string) => void
}

const INITIAL_DONATION_IDENTITY_COLLECTOR_CONTEXT: DonationIdentityCollectorContextType = {
  donorIdentity: null,
  donorIdentityId: null,
  identityIsValid: false,
  setDonorIdentity: () => {
    throw new Error('setDonorIdentity function must be overriden before use')
  },
  setDonorIdentityId: () => {
    throw new Error('setDonorIdentityId function must be overriden before use')
  },
  storePersistentDonorIdentityData: () => {
    throw new Error('storePersistentDonorIdentityData function must be overriden before use')
  }
}

export const DonationIdentityCollectorContext = createContext(
  INITIAL_DONATION_IDENTITY_COLLECTOR_CONTEXT
)

const DONOR_IDENTITY_PLACEHOLDER: DonorIdentity = {
  email: '',
  ic_number: '',
  name: '',
  phone_number: '',
  type: 'national_id'
}

const localStorageDonorIdentityData = getLocalStorageDonorIdentity()

export const DonationIdentityCollectorContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [donorIdentity, _setDonorIdentity] = useState<Nullable<Partial<DonorIdentity>>>(null)
  const [donorIdentityId, setDonorIdentityId] = useState<Nullable<string>>(null)

  // const { setDonorIdentity: setDonorIdentityInContext } = useDonationIdentityCollectorContext()

  useEffect(() => {
    if (localStorageDonorIdentityData) {
      _setDonorIdentity(localStorageDonorIdentityData.user_info || DONOR_IDENTITY_PLACEHOLDER)
      setDonorIdentityId(localStorageDonorIdentityData.user_profile_id || null)
    } else {
      _setDonorIdentity(DONOR_IDENTITY_PLACEHOLDER)
    }
  }, [])

  const setDonorIdentity = useCallback((data: Partial<DonorIdentity>) => {
    _setDonorIdentity((prev) => ({
      ...prev,
      ...data
    }))
  }, [])

  const identityIsValid = useMemo(() => {
    if (!donorIdentity || !donorIdentityId) {
      return false
    } else {
      const { email, ic_number, name, phone_number, type } = donorIdentity
      if (!email || !ic_number || !name || !phone_number || !type) {
        return false
      }
    }

    return true
  }, [donorIdentity, donorIdentityId])

  const storePersistentDonorIdentityData = useCallback((donorIdentityId: string) => {
    // if (!identityIsValid) throw new Error()

    setLocalStorageDonorIdentity({
      user_info: {...donorIdentity as DonorIdentity},
      user_profile_id: donorIdentityId
    })
  }, [donorIdentity])

  /*const saveDonorIdentityData = useCallback(() => {
    setDonorIdentityInContext(donorIdentity)
    setLocalStorageDonorIdentity(donorIdentity)
  }, [donorIdentity, setDonorIdentityInContext])*/

  return (
    <DonationIdentityCollectorContext.Provider
      value={{
        donorIdentity,
        donorIdentityId,
        identityIsValid,
        setDonorIdentity,
        setDonorIdentityId,
        storePersistentDonorIdentityData
      }}
    >
      {children}
    </DonationIdentityCollectorContext.Provider>
  )
}
